import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpenCheck, Plus, Upload, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Card, CardContent } from '../ui/card';
import IADCardFormDialog from './IADCardFormDialog';
import { API, errorText } from '../governance_v2/constants';
import { VISUAL_LABELS, MODULE_LABELS } from './constants';

const ImportButton = () => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const imp = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/iad/catalog/import`, fd); },
    onSuccess: (r) => {
      const { created, updated, errors } = r.data;
      toast[errors.length ? 'warning' : 'success'](`${created} creadas · ${updated} actualizadas${errors.length ? ` · ${errors.length} con error (${errors.map((e) => `fila ${e.row}`).join(', ')})` : ''}`);
      qc.invalidateQueries({ queryKey: ['iad-catalog'] });
    },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <>
      <input ref={ref} type="file" accept=".json,.xlsx,.xls,.csv" className="hidden" data-testid="iad-import-input"
        onChange={(e) => { if (e.target.files[0]) imp.mutate(e.target.files[0]); e.target.value = ''; }} />
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={imp.isPending} data-testid="iad-import-btn">
        <Upload className="mr-2 h-4 w-4" />{imp.isPending ? 'Importando…' : 'Importar Excel/JSON'}
      </Button>
    </>
  );
};

const CardRow = ({ card, onEdit }) => {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (v) => axios.patch(`${API}/iad/catalog/${card.code}/toggle?is_active=${v}`),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ['iad-catalog'] }); toast.success(`${card.code} ${v ? 'activado' : 'desactivado'}`); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Card className="transition-shadow duration-200 hover:shadow-md" data-testid={`iad-card-${card.code}`}>
      <CardContent className="flex items-start justify-between gap-4 p-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-bold text-amber-600 dark:text-amber-400">{card.code}</span>
            <h4 className="font-semibold text-slate-900 dark:text-white">{card.name}</h4>
            <Badge variant="outline">v{card.version}</Badge>
            {card.family && <Badge className="bg-slate-100 text-slate-700">{card.family}</Badge>}
            <Badge className="bg-sky-100 text-sky-800">{VISUAL_LABELS[card.visual_type] || card.visual_type}</Badge>
            <Badge className="bg-emerald-100 text-emerald-800">{MODULE_LABELS[card.owner_module] || 'Corporativo'}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300"><b>Para qué:</b> {card.purpose}</p>
          <p className="text-sm text-slate-500"><b>Cuándo:</b> {card.when_to_use}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Switch className="data-[state=unchecked]:bg-slate-400 dark:data-[state=unchecked]:bg-slate-600" checked={card.is_active} onCheckedChange={(v) => toggle.mutate(v)} data-testid={`iad-toggle-${card.code}`} />
          <Button size="sm" variant="outline" onClick={() => onEdit(card)} data-testid={`iad-edit-${card.code}`}>Editar</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const IADLibraryManager = () => {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['iad-catalog'], queryFn: async () => (await axios.get(`${API}/iad/catalog`)).data });
  const items = (data?.items || []).filter((c) => `${c.code} ${c.name} ${c.family} ${c.purpose}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6 p-8" data-testid="iad-library">
      <div className="flex flex-wrap items-center gap-3">
        <BookOpenCheck className="h-7 w-7 text-amber-500" />
        <div className="mr-auto">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Biblioteca Master IAD</h2>
          <p className="text-sm text-slate-500">Metodologías estratégicas (Nivel B). SUPER-100 sólo recibe su índice condensado; el método operativo lo ejecuta el especialista.</p>
        </div>
        <ImportButton />
        <Button onClick={() => setEditing({})} data-testid="iad-create-btn"><Plus className="mr-2 h-4 w-4" />Nueva ficha</Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, nombre o familia" className="pl-9" data-testid="iad-search" />
        </div>
        <span className="text-sm text-slate-500" data-testid="iad-count">{data?.count ?? 0} fichas</span>
      </div>
      <p className="text-xs text-slate-400">Importación: columnas Código, Nombre, Familia, Para qué sirve, Cuándo activarlo, Cuándo NO usarlo, Datos que requiere, Método operativo, Estructura de salida, Tipo de visual, Módulo dueño, IADs complementarios, Limitaciones, Ejemplo, Plantilla del por qué.</p>
      {isLoading && <p className="text-sm text-slate-400">Cargando biblioteca…</p>}
      <div className="space-y-2">{items.map((c) => <CardRow key={c.code} card={c} onEdit={setEditing} />)}</div>
      {editing && <IADCardFormDialog card={editing.code ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default IADLibraryManager;
